import { useState } from 'react';
import {
  Flex,
  Box,
  Button,
  Input,
} from '@chakra-ui/react';
import Header from '../components/Header';
import PageContent from '../components/PageContent';
import { useAuth } from '../hooks/useAuth';
import { deleteCurrentUser } from '../api/users';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const Profile = () => {
  const { user, updateUser, clearAuthData } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [isSaving, setIsSaving] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const hasChanges =
    firstName.trim() !== (user?.first_name || '') ||
    lastName.trim() !== (user?.last_name || '');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUser({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      toast.success(t('profile.profileUpdated'));
    } catch {
      toast.error(t('profile.updateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteCurrentUser();
      clearAuthData();
      navigate('/login');
      toast.success(t('profile.accountDeleted'));
    } catch {
      toast.error(t('profile.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const profileContent = (
    <Flex direction="column" gap="32px">
      {/* Header */}
      <Box
        fontSize="32px"
        fontWeight="500"
        color="neutral.100"
      >
        {t('profile.profile')}
      </Box>

      {/* Profile Form */}
      <Box
        backgroundColor="neutral.800"
        borderWidth="1px"
        borderColor="neutral.700"
        borderRadius="8px"
        p="24px"
      >
        <Flex direction="column" gap="24px">
          {/* First Name */}
          <Flex direction="column" rowGap="8px">
            <Box
              fontWeight="400"
              fontSize="13px"
              lineHeight="150%"
              color="neutral.400"
            >
              {t('profile.firstName')}
            </Box>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('profile.enterFirstName')}
              p="10px"
              fontWeight="400"
              fontSize="14px"
              lineHeight="150%"
              color="neutral.300"
              borderWidth="1px"
              borderColor="neutral.700"
              backgroundColor="neutral.800"
              borderRadius="6px"
              maxWidth="400px"
              _placeholder={{ color: 'neutral.500' }}
              _focus={{
                borderColor: 'neutral.600',
                outline: 'none',
              }}
            />
          </Flex>

          {/* Last Name */}
          <Flex direction="column" rowGap="8px">
            <Box
              fontWeight="400"
              fontSize="13px"
              lineHeight="150%"
              color="neutral.400"
            >
              {t('profile.lastName')}
            </Box>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('profile.enterLastName')}
              p="10px"
              fontWeight="400"
              fontSize="14px"
              lineHeight="150%"
              color="neutral.300"
              borderWidth="1px"
              borderColor="neutral.700"
              backgroundColor="neutral.800"
              borderRadius="6px"
              maxWidth="400px"
              _placeholder={{ color: 'neutral.500' }}
              _focus={{
                borderColor: 'neutral.600',
                outline: 'none',
              }}
            />
          </Flex>

          {/* Email (read-only) */}
          <Flex direction="column" rowGap="8px">
            <Box
              fontWeight="400"
              fontSize="13px"
              lineHeight="150%"
              color="neutral.400"
            >
              {t('common.email')}
            </Box>
            <Box
              fontSize="14px"
              fontWeight="400"
              color="neutral.500"
            >
              {user?.email}
            </Box>
          </Flex>

          {/* Save Button */}
          <Box>
            <Button
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasChanges || isSaving}
              height="fit-content"
              py="8px"
              px="16px"
              fontWeight="500"
              fontSize="13px"
              color="neutral.900"
              backgroundColor="neutral.100"
              borderRadius="6px"
              _hover={{
                backgroundColor: 'neutral.200',
              }}
              _disabled={{
                opacity: 0.5,
                cursor: 'not-allowed',
              }}
            >
              {t('common.saveChanges')}
            </Button>
          </Box>
        </Flex>
      </Box>

      {/* Delete Account */}
      <Box
        backgroundColor="#1C0A0A"
        borderWidth="1px"
        borderColor="#3B1C1C"
        borderRadius="8px"
        p="24px"
      >
        <Flex direction="column" gap="16px">
          <Box
            fontSize="16px"
            fontWeight="500"
            color="error.600"
          >
            {t('profile.dangerZone')}
          </Box>
          <Box
            fontSize="14px"
            fontWeight="400"
            lineHeight="150%"
            color="neutral.400"
          >
            {t('profile.deleteAccountWarning')}
          </Box>

          {!showDeleteConfirm ? (
            <Box>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                height="fit-content"
                py="8px"
                px="16px"
                fontWeight="500"
                fontSize="13px"
                color="neutral.0"
                backgroundColor="error.900"
                borderRadius="6px"
                _hover={{
                  backgroundColor: 'error.700',
                }}
              >
                {t('profile.deleteAccount')}
              </Button>
            </Box>
          ) : (
            <Flex direction="column" gap="12px">
              <Box
                fontSize="13px"
                fontWeight="400"
                color="neutral.300"
                dangerouslySetInnerHTML={{ __html: t('profile.typeEmailToConfirm', { email: user?.email }) }}
              />
              <Input
                value={deleteEmail}
                onChange={(e) => setDeleteEmail(e.target.value)}
                placeholder={t('profile.enterYourEmail')}
                p="10px"
                fontWeight="400"
                fontSize="14px"
                lineHeight="150%"
                color="neutral.300"
                borderWidth="1px"
                borderColor="neutral.700"
                backgroundColor="neutral.800"
                borderRadius="6px"
                maxWidth="400px"
                _placeholder={{ color: 'neutral.500' }}
                _focus={{
                  borderColor: 'neutral.600',
                  outline: 'none',
                }}
              />
              <Flex gap="12px">
                <Button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteEmail('');
                  }}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="13px"
                  color="neutral.300"
                  backgroundColor="transparent"
                  borderWidth="1px"
                  borderColor="neutral.600"
                  borderRadius="6px"
                  _hover={{
                    borderColor: 'neutral.500',
                    backgroundColor: 'neutral.700',
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  loading={isDeleting}
                  disabled={deleteEmail !== user?.email || isDeleting}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="13px"
                  color="neutral.0"
                  backgroundColor="error.900"
                  borderRadius="6px"
                  _hover={{
                    backgroundColor: 'error.700',
                  }}
                  _disabled={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                >
                  {t('profile.confirmDeletion')}
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Box>
    </Flex>
  );

  return (
    <Flex minHeight="100vh" direction="column" backgroundColor="neutral.900">
      <Header />

      <Flex flex="1" direction="column">
        <PageContent>
          {profileContent}
        </PageContent>
      </Flex>
    </Flex>
  );
};

export default Profile;
